import { useEffect } from 'react';
import { FIELD_OPS_ROUTES } from '../../../constants';
import {
  BeatPlanStatus,
  BeatPlanTypeEnum,
  dispatch,
  ErrorVariants,
  getExtraDetails,
  getNonPeriodicBeatPlanInfo,
  getPeriodicBeatPlanInfo,
  getUserData,
  getWorkspace,
  showToast,
  useAppSelector,
  useCreateBeatPlanByCustomerMutation,
  useUpdateBeatPlanMutation,
} from '@zonofi/zono-money-store';
import { useNavigate } from 'react-router-dom';
import { addCustomerModalGroupRef } from '../modals';
import { useResetFlow } from '../../hook';

export const useAssignBeatPlan = () => {
  const navigate = useNavigate();
  const workspace = useAppSelector(getWorkspace);
  const customerDetails = useAppSelector(getExtraDetails);
  const nonPeriodicBeatPlanInfo = useAppSelector(getNonPeriodicBeatPlanInfo);
  const periodicBeatPlanInfo = useAppSelector(getPeriodicBeatPlanInfo);
  const inviteId = useAppSelector(getUserData)?.inviteId;

  const [updateBeatPlanApi, updateBeatPlanApiResponse] =
    useUpdateBeatPlanMutation();
  const [createBeatPlanByCustomer, createBeatPlanByCustomerResponse] =
    useCreateBeatPlanByCustomerMutation();

  const { handleBack } = useResetFlow();

  const handleSuccess = () => {
    handleBack();
    setTimeout(() => {
      addCustomerModalGroupRef?.current?.openSuccessModal();
    }, 100);
  };

  const handleError = () => {
    dispatch(
      showToast({
        message: 'Failed to create the beat plan for the customer',
        type: ErrorVariants.DANGER,
        canDisplay: true,
        responseStatus: 'Error',
      })
    );
  };

  const isCustomerAlreadyInBeatPlan = () => {
    if (
      !nonPeriodicBeatPlanInfo?.beatPlanScheduleCustomers ||
      !customerDetails?.customerId
    ) {
      return false;
    }

    return nonPeriodicBeatPlanInfo.beatPlanScheduleCustomers.some(
      (schedule) => schedule.customerId === customerDetails.customerId
    );
  };

  const buildBeatPlanSchedules = () => {
    if (!nonPeriodicBeatPlanInfo || !customerDetails || !inviteId) return [];

    const isRouteBeatPlan =
      nonPeriodicBeatPlanInfo?.beatType === BeatPlanTypeEnum.Routes;
    const existingSchedules = isRouteBeatPlan
      ? (nonPeriodicBeatPlanInfo.beatPlanScheduleRoutes ?? [])
      : (nonPeriodicBeatPlanInfo.beatPlanScheduleCustomers ?? []);

    const newSchedule = {
      beatPlanId: nonPeriodicBeatPlanInfo.id,
      ...(isRouteBeatPlan
        ? { routeId: customerDetails?.routeCustomerGroup?.routeId }
        : { customerId: customerDetails?.customerId }),
      teamMemberId: inviteId,
      visitOrder: 1,
      scheduleDate: null,
      scheduleStatus: null,
      dayName: null,
      notes: null,
    };

    return [
      ...existingSchedules.map((s, index) => ({
        id: s.id,
        beatPlanId: s.beatPlanId,
        ...(isRouteBeatPlan
          ? { routeId: s.routeId }
          : { customerId: s.customerId }),
        teamMemberId: s.teamMemberId,
        visitOrder: index + 1,
        scheduleDate: s.scheduleDate ?? null,
        scheduleStatus: s.scheduleStatus ?? null,
        dayName: s.dayName ?? null,
        notes: s.notes ?? null,
      })),
      newSchedule,
    ];
  };

  const onCreateAndAssignBeatPlan = () => {
    if (!workspace?.id) return;
    if (nonPeriodicBeatPlanInfo?.id) {
      if (nonPeriodicBeatPlanInfo.beatType === BeatPlanTypeEnum.Routes) {
        handleSuccess();
      } else if (
        nonPeriodicBeatPlanInfo.beatType === BeatPlanTypeEnum.Customers
      ) {
        if (isCustomerAlreadyInBeatPlan()) {
          dispatch(
            showToast({
              message: 'Customer already exists in this beat plan',
              type: ErrorVariants.DANGER,
              canDisplay: true,
              responseStatus: 'Error',
            })
          );

          setTimeout(() => {
            navigate(FIELD_OPS_ROUTES.VIEW_CUSTOMER_LIST);
          }, 200);
          return;
        }
        const beatPlanSchedules = buildBeatPlanSchedules();
        if (!beatPlanSchedules.length) return;

        updateBeatPlanApi({
          sellerWorkspaceId: workspace.id,
          beatPlanId: nonPeriodicBeatPlanInfo.id,
          body: {
            beatPlanSchedules,
          },
        });
      }
    } else if (periodicBeatPlanInfo === BeatPlanTypeEnum.Routes) {
      handleSuccess();
    } else {
      createBeatPlanByCustomer({
        sellerWorkspaceId: workspace.id,
        body: {
          planName: '',
          startDate: null,
          endDate: null,
          isPeriodic: false,
          frequency: null,
          beatType: BeatPlanTypeEnum.Customers,
          status: BeatPlanStatus.Active,
          beatPlanSchedules: [
            {
              routeId: null,
              customerId: customerDetails?.customerId,
              teamMemberId: inviteId as string,
              visitOrder: 1,
            },
          ],
        },
      });
    }
  };

  useEffect(() => {
    if (updateBeatPlanApiResponse.isSuccess && updateBeatPlanApiResponse.data) {
      handleSuccess();
    }
    if (updateBeatPlanApiResponse.isError) {
      handleError();
    }
  }, [updateBeatPlanApiResponse]);

  useEffect(() => {
    if (
      createBeatPlanByCustomerResponse.isSuccess &&
      createBeatPlanByCustomerResponse.data
    ) {
      handleSuccess();
    }
    if (createBeatPlanByCustomerResponse.isError) {
      handleError();
    }
  }, [createBeatPlanByCustomerResponse]);

  const onEditRoutePress = () => {
    navigate(FIELD_OPS_ROUTES.ASSIGN_ROUTES);
  };

  const isBeatPlanByRoute =
    (nonPeriodicBeatPlanInfo?.beatType ?? periodicBeatPlanInfo) ===
    BeatPlanTypeEnum.Routes;

  return {
    customerName: workspace?.spaceName,
    customerDetails,
    isBeatPlanByRoute: isBeatPlanByRoute,
    isloading:
      updateBeatPlanApiResponse?.isLoading ||
      createBeatPlanByCustomerResponse?.isLoading,
    onCreateAndAssignBeatPlan,
    onEditRoutePress,
    periodicBeatPlanInfo,
  };
};
